import { formatScore } from "@/app/(app)/_lib/format";

type ScoreChipProps = {
  score: number;
  /** Vertical padding. Default matches the table-cell chip (py-1). */
  size?: "sm" | "md";
};

export function ScoreChip({ score, size = "md" }: ScoreChipProps) {
  // Pick the color from the SAME rounded value we display (formatScore = 2 dp),
  // not the raw score. Otherwise two matches that both read "0.95" can get
  // different colors — a raw 0.9487 displays "0.95" but fell in the yellow band
  // while 0.9512 went red. The chip color must always match the number shown.
  const shown = Number(formatScore(score));
  const tone =
    shown >= 0.95
      ? "bg-red-100 text-red-600"
      : shown >= 0.91
        ? "bg-yellow-100 text-yellow-700"
        : "bg-grey-100 text-grey-900";
  const sizing = size === "sm" ? "h-6 gap-1.5" : "py-1 gap-1";
  return (
    <span
      className={`inline-flex w-fit items-center rounded-[4px] px-2 text-small font-medium ${sizing} ${tone}`}
    >
      <ScorePie score={score} />
      {formatScore(score)}
    </span>
  );
}

function ScorePie({ score }: { score: number }) {
  // Fill the ring to the score itself, so the gauge reads as the number
  // (0.90 → 90% filled, not a remapped 50%).
  const f = Math.max(0, Math.min(1, score));
  const cx = 8;
  const cy = 8;
  const r = 6;
  const circumference = 2 * Math.PI * r;
  const dash = f * circumference;
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden className="shrink-0">
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        opacity="0.25"
      />
      {dash > 0 && (
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      )}
    </svg>
  );
}
